import { motion } from "framer-motion";
import { Smartphone } from "lucide-react";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const WORLD_MAP =
  "https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg";

const services = [
  {
    n: "01",
    title: "Positioning",
    body: "We build your brand messaging, collaterals, and marketing activities around the learners you want to acquire, so your brand becomes easier to notice, trust, and choose in a crowded education market.",
  },
  {
    n: "02",
    title: "Performance",
    body: "We build the right tracking across your campaigns, content, landing pages, and learner acquisition channels, so no learner goes untracked and your team can make better growth decisions with clearer data.",
  },
  {
    n: "03",
    title: "Precision",
    body: "We build targeted engagement systems for learners already inside your funnel, so you can create stronger relationships and convert prospective learners with more confidence.",
  },
];

const AV = "?auto=format&fit=crop&crop=faces&w=200&q=80";
const learners = [
  { src: `https://images.unsplash.com/photo-1573496359142-b8d87734a5a2${AV}`, top: "8%", left: "20%" },
  { src: `https://images.unsplash.com/photo-1560250097-0b93528c311a${AV}`, top: "24%", left: "58%" },
  { src: `https://images.unsplash.com/photo-1580489944761-15a19d654956${AV}`, top: "50%", left: "38%" },
  { src: `https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d${AV}`, top: "18%", left: "82%" },
  { src: `https://images.unsplash.com/photo-1494790108377-be9c29b29330${AV}`, top: "66%", left: "72%" },
  { src: `https://images.unsplash.com/photo-1500648767791-00dcc994a43e${AV}`, top: "72%", left: "12%" },
];

export const Services = () => {
  return (
    <section id="services" className="bg-[#FAF7F2] text-[#1A2238] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="max-w-3xl">
          <motion.p variants={fadeUp} className="eyebrow text-[#FF6A3D] mb-6">
            Services
          </motion.p>
          <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.05]">
            Get Seen, Trusted, and Chosen by the Right Learners
          </motion.h2>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Numbered service list */}
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="space-y-8">
            {services.map((s) => (
              <motion.div
                variants={fadeUp}
                key={s.title}
                data-testid={`service-${s.title.toLowerCase()}`}
                className="flex gap-5"
              >
                <div className="shrink-0">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1A2238] text-[#FF6A3D] font-display font-extrabold text-lg">
                    {s.n}
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2.5 text-[15px] text-[#1A2238]/65 leading-relaxed max-w-xl">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* World map with learner avatars */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="relative"
            data-testid="services-map"
          >
            <div className="relative aspect-[4/3] w-full">
              <img
                src={WORLD_MAP}
                alt="Global learner reach"
                className="absolute inset-0 h-full w-full object-contain opacity-[0.14] grayscale"
              />
              {learners.map((l, i) => (
                <motion.div
                  key={i}
                  className="absolute floaty"
                  style={{ top: l.top, left: l.left, animationDelay: `${i * 0.7}s` }}
                  initial={{ opacity: 0, scale: 0.6 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.12 }}
                >
                  <div className="relative">
                    <img
                      src={l.src}
                      alt="Learner"
                      className="h-12 w-12 lg:h-14 lg:w-14 rounded-full object-cover ring-2 ring-[#FF6A3D] shadow-[0_8px_24px_rgba(26,34,56,0.2)]"
                    />
                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF6A3D] ring-2 ring-[#FAF7F2]">
                      <Smartphone size={12} className="text-[#1A2238]" />
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-center text-sm text-[#1A2238]/50 font-medium">
              Learners across the globe choosing education brands, one tap at a time.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
